import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

/** The title block every dashboard screen opens with. */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  eyebrow?: string
}) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-3 sm:mb-6 sm:gap-4">
      <div className="min-w-0">
        {eyebrow && <p className="label-eyebrow mb-1.5">{eyebrow}</p>}
        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[26px]">
          {title}
        </h1>
        {description && (
          <p className="mt-1 max-w-[620px] text-[13.5px] leading-relaxed text-ink-muted sm:text-[14px]">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">{actions}</div>}
    </header>
  )
}

/** A card with a titled header row; the standard container for a page section. */
export function Section({
  id,
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
}: {
  id?: string
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}) {
  return (
    <section id={id} className={cn('card scroll-mt-24 overflow-hidden', className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            {title && <h2 className="text-[16px] font-semibold text-ink">{title}</h2>}
            {description && <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={bodyClassName ?? 'p-5'}>{children}</div>
    </section>
  )
}

const TONES = {
  neutral: 'text-ink',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
} as const

/** One number with its label: the dashboard's at-a-glance row. */
export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  onClick,
  active,
  icon,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  tone?: keyof typeof TONES
  onClick?: () => void
  active?: boolean
  icon?: ReactNode
}) {
  const body = (
    <>
      <p className="flex items-center justify-between gap-2 text-[13px] font-medium text-ink-muted">
        {label}
        {icon && <span className={cn('opacity-70', TONES[tone])}>{icon}</span>}
      </p>
      <p className={cn('num mt-1 text-[26px] font-semibold leading-none', TONES[tone])}>
        {value}
      </p>
      {hint && <p className="mt-2 text-[12px] text-ink-faint">{hint}</p>}
    </>
  )
  const base = cn(
    'card px-4 py-3.5 text-left sm:px-5 sm:py-4',
    active && 'border-brand/40 ring-2 ring-brand/15',
  )
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(base, 'transition hover:-translate-y-px hover:shadow-float')}
      >
        {body}
      </button>
    )
  }
  return <div className={base}>{body}</div>
}

/** A thin horizontal meter; `value` is 0–100. */
export function ProgressBar({
  value,
  label,
  tone = 'brand',
  className,
}: {
  value: number
  label?: string
  tone?: 'brand' | 'warning' | 'danger' | 'ink'
  className?: string
}) {
  const clamped = Math.min(Math.max(value, 0), 100)
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn('h-1.5 w-full overflow-hidden rounded-pill bg-ink/[0.07]', className)}
    >
      <div
        className={cn(
          'h-full rounded-pill transition-[width] duration-500',
          { brand: 'bg-brand', warning: 'bg-warning', danger: 'bg-danger', ink: 'bg-ink' }[tone],
        )}
        style={{ width: `${clamped}%` }}
      />
    </div>
  )
}
